import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    OutlinedInput,
    CircularProgress,
    Alert,
    Card,
    Stack,
    Snackbar,
    Divider,
    Checkbox,
    Container,
    Button
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { fetchAllWorkcells, updateWorkcell } from "../api";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";
import { formatLocalDate } from "../utils/localDateTime";

const MonthlyOverviewButton = ({ workcellId }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const handleClick = () => {
        const today = formatLocalDate(new Date());
        navigate(`/chart/workcell/${workcellId}/monthly/${today}`);
    };
    return (
        <Button variant="outlined" size="small" onClick={handleClick}>
            {t("workcells.manage.actions.view")}
        </Button>
    );
};

const WorkcellManagementPage = () => {
    const { t } = useTranslation();
    const { currencySymbol } = useCustomerCurrency();
    const [workcells, setWorkcells] = useState([]);
    const [hourlyRates, setHourlyRates] = useState({});
    const [oeeFlags, setOeeFlags] = useState({});
    const [operationRates, setOperationRates] = useState({});
    const [targetOees, setTargetOees] = useState({});
    const [workcellNames, setWorkcellNames] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savedStatus, setSavedStatus] = useState({});
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchAllWorkcells();
            setWorkcells(data);
            const rates = {};
            const flags = {};
            const opRates = {};
            const targetOeeValues = {};
            const names = {};
            data.forEach(workcell => {
                rates[workcell.workcell_id] = workcell.hourly_rate;
                flags[workcell.workcell_id] = workcell.has_oee;
                opRates[workcell.workcell_id] = workcell.operation_rate;
                targetOeeValues[workcell.workcell_id] = (Number(workcell.target_oee) || 0) * 100;
                names[workcell.workcell_id] = workcell.workcell_name;
            });
            setHourlyRates(rates);
            setOeeFlags(flags);
            setOperationRates(opRates);
            setTargetOees(targetOeeValues);
            setWorkcellNames(names);
            setError(null);
        } catch (err) {
            console.error("Error fetching workcells:", err);
            setError(t("workcells.manage.errors.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleNameChange = (id, name) => {
        setWorkcellNames({ ...workcellNames, [id]: name });
        if (savedStatus[id]) {
            setSavedStatus({ ...savedStatus, [id]: false });
        }
    };

    const handleSaveName = async (id) => {
        try {
            const workcell_name = workcellNames[id];
            if (!workcell_name) return;

            await updateWorkcell(id, { workcell_name });
            setSavedStatus({ ...savedStatus, [id]: true });
            setSnackbarOpen(true);
            setTimeout(() => {
                setSavedStatus((prev) => ({ ...prev, [id]: false }));
            }, 2000);
        } catch (error) {
            console.error("Failed to update workcell name:", error);
            setError(t("workcells.manage.errors.updateNameFailed"));
        }
    };

    const handleNameKeyDown = (event, id) => {
        if (event.key === 'Enter') {
            event.target.blur();
            handleSaveName(id);
        }
    };

    const handleHourlyRateChange = (id, rate) => {
        setHourlyRates({ ...hourlyRates, [id]: rate });
        if (savedStatus[id]) {
            setSavedStatus({ ...savedStatus, [id]: false });
        }
    };

    const handleOeeChange = async (id) => {
        const newValue = !oeeFlags[id];
        setOeeFlags({ ...oeeFlags, [id]: newValue });
        try {
            await updateWorkcell(id, { has_oee: newValue });
            setSnackbarOpen(true);
            setSavedStatus({ ...savedStatus, [id]: true });
            setTimeout(() => {
                setSavedStatus((prev) => ({ ...prev, [id]: false }));
            }, 2000);
        } catch (error) {
            console.error("Failed to update OEE flag:", error);
            setError(t("workcells.manage.errors.updateOeeFailed"));
            setOeeFlags({ ...oeeFlags, [id]: !newValue }); // Revert on error
        }
    };

    const handleSaveHourlyRate = async (id) => {
        try {
            const hourly_rate = parseFloat(hourlyRates[id]);
            if (isNaN(hourly_rate)) return;

            await updateWorkcell(id, { hourly_rate });
            setSavedStatus({ ...savedStatus, [id]: true });
            setSnackbarOpen(true);
            setTimeout(() => {
                setSavedStatus((prev) => ({ ...prev, [id]: false }));
            }, 2000);
        } catch (error) {
            console.error("Failed to update internal hourly rate:", error);
            setError(t("workcells.manage.errors.updateHourlyRateFailed"));
        }
    };

    const handleHourlyRateKeyDown = (event, id) => {
        if (event.key === 'Enter') {
            event.target.blur();
            handleSaveHourlyRate(id);
        }
    };

    const handleOperationRateChange = (id, rate) => {
        setOperationRates({ ...operationRates, [id]: rate });
        if (savedStatus[id]) {
            setSavedStatus({ ...savedStatus, [id]: false });
        }
    };

    const handleSaveOperationRate = async (id) => {
        try {
            const operation_rate = parseFloat(operationRates[id]);
            if (isNaN(operation_rate)) return;
            await updateWorkcell(id, { operation_rate });
            setSavedStatus({ ...savedStatus, [id]: true });
            setSnackbarOpen(true);
            setTimeout(() => {
                setSavedStatus((prev) => ({ ...prev, [id]: false }));
            }, 2000);
        } catch (error) {
            console.error("Failed to update operation rate:", error);
            setError(t("workcells.manage.errors.updateOperationRateFailed"));
        }
    };

    const handleOperationRateKeyDown = (event, id) => {
        if (event.key === 'Enter') {
            event.target.blur();
            handleSaveOperationRate(id);
        }
    };

    const handleTargetOeeChange = (id, value) => {
        setTargetOees({ ...targetOees, [id]: value });
        if (savedStatus[id]) {
            setSavedStatus({ ...savedStatus, [id]: false });
        }
    };

    const handleSaveTargetOee = async (id) => {
        try {
            const target_oee_percent = parseFloat(targetOees[id]);
            if (isNaN(target_oee_percent)) return;
            const target_oee = target_oee_percent / 100;
            await updateWorkcell(id, { target_oee });
            setSavedStatus({ ...savedStatus, [id]: true });
            setSnackbarOpen(true);
            setTimeout(() => {
                setSavedStatus((prev) => ({ ...prev, [id]: false }));
            }, 2000);
        } catch (error) {
            console.error("Failed to update target OEE:", error);
            setError(t("workcells.manage.errors.updateTargetOeeFailed"));
        }
    };

    const handleTargetOeeKeyDown = (event, id) => {
        if (event.key === 'Enter') {
            event.target.blur();
            handleSaveTargetOee(id);
        }
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
        </Box>
    );

    if (error) return (
        <Alert severity="error" sx={{ m: 2 }}>
            {error}
        </Alert>
    );

    return (
        <Container>
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h4">
                        {t("workcells.manage.title")}
                    </Typography>
                </Box>

                {workcells.length === 0 ? (
                    <Typography variant="body1" align="center" sx={{ p: 3, color: 'text.secondary' }}>
                        {t("workcells.manage.empty")}
                    </Typography>
                ) : (
                    <Card variant="outlined" sx={{ borderRadius: 1 }}>
                        <Stack divider={<Divider />}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    gap: 2,
                                    height: 48
                                }}
                            >
                                <span style={{ flexGrow: 1, fontWeight: 'bold' }}>{t("workcells.manage.table.name")}</span>
                                <Box width={60} display="flex" justifyContent="center">
                                    <span style={{ fontWeight: 'bold' }}>{t("workcells.manage.table.active")}</span>
                                </Box>
                                <Box width={120} display="flex" justifyContent="center">
                                    <span style={{ fontWeight: 'bold' }}>{t("workcells.manage.table.hourlyRate")}</span>
                                </Box>
                                <Box width={120} display="flex" justifyContent="center">
                                    <span style={{ fontWeight: 'bold' }}>{t("workcells.manage.table.operationRate")}</span>
                                </Box>
                                <Box width={120} display="flex" justifyContent="center">
                                    <span style={{ fontWeight: 'bold' }}>{t("workcells.manage.table.targetOee")}</span>
                                </Box>
                                <Box width={100} display="flex" justifyContent="center">
                                    <span style={{ fontWeight: 'bold' }}>{t("workcells.manage.table.overview")}</span>
                                </Box>
                            </Box>

                            {workcells.map(workcell => (
                                <Box
                                    key={workcell.workcell_id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2,
                                        gap: 2,
                                        height: 48,
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <Box flexGrow={1} height={40}>
                                        <OutlinedInput
                                            value={workcellNames[workcell.workcell_id]}
                                            onChange={(e) => handleNameChange(workcell.workcell_id, e.target.value)}
                                            onBlur={() => handleSaveName(workcell.workcell_id)}
                                            onKeyDown={(e) => handleNameKeyDown(e, workcell.workcell_id)}
                                            size="small"
                                            fullWidth
                                            endAdornment={savedStatus[workcell.workcell_id] && (
                                                <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                                            )}
                                            sx={{
                                                height: '40px',
                                                '&:hover fieldset': { borderColor: 'primary.main' }
                                            }}
                                        />
                                    </Box>
                                    <Box width={60} display="flex" justifyContent="center" alignItems="center">
                                        <Checkbox
                                            checked={oeeFlags[workcell.workcell_id] || false}
                                            onChange={() => handleOeeChange(workcell.workcell_id)}
                                            color="primary"
                                            size="medium"
                                        />
                                    </Box>
                                    <Box width={120} height={40}>
                                        <OutlinedInput
                                            type="number"
                                            value={hourlyRates[workcell.workcell_id]}
                                            onChange={(e) => handleHourlyRateChange(workcell.workcell_id, e.target.value)}
                                            onBlur={() => handleSaveHourlyRate(workcell.workcell_id)}
                                            onKeyDown={(e) => handleHourlyRateKeyDown(e, workcell.workcell_id)}
                                            size="small"
                                            fullWidth
                                            inputProps={{ min: 0, step: 0.01 }}
                                            startAdornment={<span>{currencySymbol}</span>}
                                            endAdornment={savedStatus[workcell.workcell_id] && (
                                                <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                                            )}
                                            sx={{
                                                height: '40px',
                                                '& input': { textAlign: 'right' },
                                                '&:hover fieldset': { borderColor: 'primary.main' }
                                            }}
                                        />
                                    </Box>
                                    <Box width={120} height={40}>
                                        <OutlinedInput
                                            type="number"
                                            value={operationRates[workcell.workcell_id]}
                                            onChange={(e) => handleOperationRateChange(workcell.workcell_id, e.target.value)}
                                            onBlur={() => handleSaveOperationRate(workcell.workcell_id)}
                                            onKeyDown={(e) => handleOperationRateKeyDown(e, workcell.workcell_id)}
                                            size="small"
                                            fullWidth
                                            inputProps={{ min: 0, step: 0.01 }}
                                            startAdornment={<span>{currencySymbol}</span>}
                                            endAdornment={savedStatus[workcell.workcell_id] && (
                                                <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                                            )}
                                            sx={{
                                                height: '40px',
                                                '& input': { textAlign: 'right' },
                                                '&:hover fieldset': { borderColor: 'primary.main' }
                                            }}
                                        />
                                    </Box>
                                    <Box width={120} height={40}>
                                        <OutlinedInput
                                            type="number"
                                            value={targetOees[workcell.workcell_id]}
                                            onChange={(e) => handleTargetOeeChange(workcell.workcell_id, e.target.value)}
                                            onBlur={() => handleSaveTargetOee(workcell.workcell_id)}
                                            onKeyDown={(e) => handleTargetOeeKeyDown(e, workcell.workcell_id)}
                                            size="small"
                                            fullWidth
                                            inputProps={{ step: 0.01 }}
                                            endAdornment={(
                                                <>
                                                    <span>%</span>
                                                    {savedStatus[workcell.workcell_id] && (
                                                        <CheckCircleIcon color="success" fontSize="small" sx={{ ml: 0.5 }} />
                                                    )}
                                                </>
                                            )}
                                            sx={{
                                                height: '40px',
                                                '& input': { textAlign: 'right' },
                                                '&:hover fieldset': { borderColor: 'primary.main' }
                                            }}
                                        />
                                    </Box>
                                    <Box width={100} display="flex" alignItems="center" justifyContent="center">
                                        <MonthlyOverviewButton workcellId={workcell.workcell_id} />
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Card>
                )}

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={2000}
                    onClose={() => setSnackbarOpen(false)}
                    message={t("workcells.manage.snackbar.updated")}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
            </Box>
        </Container>
    );
};

export default WorkcellManagementPage;
